import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { CultivWordmark } from "@/components/EdenWordmark";

/**
 * "How Cultiv Speaks" — the public statement of the language and experience system.
 * Read at /how-we-speak. Linked from the landing footer.
 * Governed by /app/CULTIV_LANGUAGE_SYSTEM.md — the founding statement below is the north star.
 */

const Section = ({ eyebrow, heading, children }) => (
    <section className="border-t border-foreground/10">
        <div className="max-w-3xl mx-auto px-6 md:px-0 py-16 md:py-24">
            <div className="eyebrow-gold mb-4">{eyebrow}</div>
            <h2 className="font-serif text-3xl md:text-4xl leading-[1.1] mb-6">{heading}</h2>
            <div className="prose-cultiv">{children}</div>
        </div>
    </section>
);

export default function HowWeSpeak() {
    return (
        <div className="min-h-screen bg-background text-foreground grain">
            <header className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 flex items-center">
                <Link to="/" className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.32em] text-muted-foreground hover:text-foreground transition-colors" data-testid="back-home">
                    <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back
                </Link>
                <div className="ml-auto">
                    <CultivWordmark size="sm" tone="forest" />
                </div>
            </header>

            <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-3xl mx-auto px-6 md:px-0 pt-16 pb-24 md:pt-24 md:pb-32"
            >
                <div className="eyebrow mb-6">How Cultiv Speaks</div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">
                    A short statement of the way we write, and why.
                </h1>
                <p className="mt-8 prose-cultiv">
                    Cultiv is not a productivity app. It is not a self-improvement app. It is not a Christian version of a
                    habit tracker. Cultiv exists to help people faithfully steward every area of the life God has entrusted
                    to them. The goal is not optimization. The goal is faithful stewardship. This page names how we try to
                    write in service of that.
                </p>
            </motion.section>

            <Section eyebrow="The garden entrusted to you" heading="One garden, given by God, cultivated as one life.">
                <p>
                    Heart, Body, Mind, Home, Relationships, Work &amp; Calling, Rest, and Kingdom are not eight separate
                    plots to manage. They are interconnected areas of the garden God has entrusted to you. When Rest
                    thins, Mind follows. When Relationships flourish, Heart often flourishes with them. Cultiv earns
                    its value by naming the connections between areas — and by remembering, at every turn, that this
                    garden was received before it was tended.
                </p>
            </Section>

            <Section eyebrow="Voice" heading="Clear before it is clever.">
                <p>
                    Cultiv aims to sound like a wise mentor helping you understand your own life through the lens of
                    biblical stewardship. Thoughtful. Clear. Warm. Practical. Biblical. Humble. Rich without being wordy.
                </p>
                <p>
                    What Cultiv is not: AI-flavored, corporate, devotional-fluffy, mystical, or vaguely poetic. If a
                    sentence is vague, we rewrite it. Clarity is always more valuable than cleverness.
                </p>
            </Section>

            <Section eyebrow="Insight framework" heading="Every observation moves through five layers.">
                <p>
                    Whenever Cultiv tells you something meaningful — in the Blueprint, in Tend, in the daily encouragement,
                    on the dashboard — the writing moves in this order:
                </p>
                <ol className="mt-6 space-y-4 list-none pl-0">
                    <li><span className="eyebrow-gold mr-3">Observation</span> What we actually see, plainly.</li>
                    <li><span className="eyebrow-gold mr-3">Pattern</span> The relationship between two or more areas of the garden entrusted to you.</li>
                    <li><span className="eyebrow-gold mr-3">Biblical Interpretation</span> One named passage, taught in one sentence — never as decoration.</li>
                    <li><span className="eyebrow-gold mr-3">Stewardship Response</span> One achievable step for this week — never generic.</li>
                    <li><span className="eyebrow-gold mr-3">Prayer</span> Grounded in the observation, when the moment calls for it.</li>
                </ol>
            </Section>

            <Section eyebrow="Discernment" heading="The goal is not your dependence on Cultiv.">
                <p>
                    Cultiv exists to help you grow in discernment — the capacity to recognize biblical patterns in your own
                    life without help. Every insight is written to teach you how to see this again on your own. If you use
                    Cultiv faithfully for years, you should gradually need less explanation because your discernment has
                    grown. That is not a threat to the platform — it is the platform's purpose.
                </p>
            </Section>

            <Section eyebrow="Scripture" heading="Scripture teaches. It does not decorate.">
                <p>
                    Cultiv never attaches a verse simply because it contains a similar word. Every Scripture we cite must
                    genuinely illuminate the specific observation it accompanies. We name the passage, quote it from the
                    King James Version, and teach the principle in one or two sentences. Scripture interprets life; life
                    does not interpret Scripture.
                </p>
            </Section>

            <Section eyebrow="Premium" heading="Premium is continued cultivation — not locked content.">
                <p>
                    The free experience of Cultiv is complete on its own. A free user should feel seen, not withheld from.
                    Premium is for the person who has reached the edge of the summary and now needs the full Stewardship
                    Blueprint. It is the same voice, continued for as long as this season requires. We do not use the word
                    "unlock" here.
                </p>
            </Section>

            <Section eyebrow="Growth stages" heading="States and seasons — not gardening chores.">
                <p>
                    Cultiv teaches five biblical states a soul may be in — Seed, Rooting, Tender Growth, Forming,
                    Fruitfulness — and three seasons that can arrive in any state — Pruning, Wintering, and Restoration.
                    A tree does not "enter the Pruning stage"; pruning happens <em>to</em> a fruitful branch. Cultiv
                    respects that biblical distinction and never uses agricultural language decoratively.
                </p>
            </Section>

            <Section eyebrow="Modules" heading="Every future module inherits this voice.">
                <p>
                    Marriage. Family. Home. Financial Stewardship. Calling. Whatever comes next in Cultiv will speak in the
                    same voice, teach through the same framework, and honor the same principle: the garden God has
                    entrusted to you, faithfully
                    stewarded.
                </p>
            </Section>

            <section className="border-t border-foreground/10 bg-[hsl(var(--eden-forest-deep))] text-[hsl(var(--eden-cream))]">
                <div className="max-w-3xl mx-auto px-6 md:px-0 py-24 md:py-32 text-center">
                    <div className="eyebrow-gold mb-8">The founding statement</div>
                    <blockquote className="font-serif text-3xl md:text-5xl leading-[1.1]">
                        Cultiv does not exist to tell people who they are.
                        <br />
                        It exists to help them faithfully steward who God is forming them into.
                    </blockquote>
                    <div className="mt-16 eyebrow-gold" style={{ letterSpacing: "0.4em" }}>
                        Formed by Him · Filled by Him · Established by Him
                    </div>
                </div>
            </section>
        </div>
    );
}
